import type { Tunables } from '../sim/tunables';

/**
 * Resolution scaling: measures fps over a window, steps the pixel ratio down while the device misses the
 * target and back up when it has headroom again, so one slow moment does not leave the match blurry.
 * - The first seconds (loading, shader compiles) are ignored.
 * - It never goes below 1x (or the device's own ratio, if lower), so the picture stays sharp.
 * - It does not step back up to a ratio that just proved too slow until that ratio has had a rest, and each
 *   time the same ratio fails again the rest doubles, so a device on the edge does not stutter on a timer.
 */
export class QualityGovernor {
  pixelRatio: number;
  fps = 0;
  private readonly maxRatio: number;
  private readonly minRatio: number;
  private frames = 0;
  private elapsed = 0;
  private age = 0;
  /** Lowest ratio that was too slow, and how long ago; stepping up stops below it for a while. */
  private tooSlowAt = Infinity;
  private sinceTooSlow = 0;
  /** The ratio whose retry last failed, and the rest before the next retry (grows each time it fails again). */
  private failedRatio = 0;
  private retryWait: number;

  constructor(
    private readonly t: Tunables,
    devicePixelRatio: number,
  ) {
    this.maxRatio = Math.min(devicePixelRatio, t.render.maxPixelRatio);
    this.minRatio = Math.min(this.maxRatio, t.render.minPixelRatio);
    this.pixelRatio = this.maxRatio;
    this.retryWait = t.render.retrySeconds;
  }

  /** Feed one frame's duration in seconds. Returns true when the pixel ratio changed. */
  tick(dt: number): boolean {
    const r = this.t.render;
    // A long gap means the tab was hidden or the page stalled, not that the device is slow.
    if (dt > r.maxFrameSeconds) return false;
    this.age += dt;
    if (this.age < r.warmupSeconds) return false;
    this.sinceTooSlow += dt;
    if (this.sinceTooSlow > this.retryWait) this.tooSlowAt = Infinity;
    this.frames++;
    this.elapsed += dt;
    if (this.elapsed < r.sampleSeconds) return false;
    this.fps = this.frames / this.elapsed;
    this.frames = 0;
    this.elapsed = 0;
    if (this.fps < r.targetFps && this.pixelRatio > this.minRatio) {
      // The same ratio failing again means the device sits on the edge: wait longer before the next try.
      this.retryWait = this.pixelRatio === this.failedRatio ? this.retryWait * r.retryBackoff : r.retrySeconds;
      this.failedRatio = this.pixelRatio;
      this.tooSlowAt = Math.min(this.tooSlowAt, this.pixelRatio);
      this.sinceTooSlow = 0;
      this.pixelRatio = Math.max(this.minRatio, this.pixelRatio - r.pixelRatioStep);
      return true;
    }
    const up = Math.min(this.maxRatio, this.pixelRatio + r.pixelRatioStep);
    if (this.fps >= r.stepUpFps && up > this.pixelRatio && up < this.tooSlowAt) {
      this.pixelRatio = up;
      return true;
    }
    return false;
  }
}
